import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ArrowLeft, Users, CheckCircle2, Lock, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CamperChip = ({ camper, selected, onClick, testId, color }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-all active:scale-95 text-left ${
      selected
        ? "border-amber-500 bg-amber-100 text-amber-900 ring-2 ring-amber-300"
        : color === "blue"
        ? "border-blue-200 bg-blue-50 text-blue-900 hover:border-blue-400"
        : color === "red"
        ? "border-red-200 bg-red-50 text-red-900 hover:border-red-400"
        : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
    }`}
  >
    {camper.name}
    {camper.missing_days && camper.missing_days.length > 0 && (
      <span className="block text-[10px] font-normal text-orange-600">
        not here {camper.missing_days.join(", ")}
      </span>
    )}
  </button>
);

const SavedTeams = ({ saved }) => (
  <div>
    <div className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border-2 border-emerald-300" data-testid="teams-saved-banner">
      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
      <div>
        <p className="text-sm font-bold text-emerald-800">Teams saved</p>
        <p className="text-xs text-emerald-700 flex items-center gap-1"><Lock className="w-3 h-3" /> Contact the office if a change is needed.</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[["Team 1", saved.team1, "blue"], ["Team 2", saved.team2, "red"]].map(([label, list, c]) => (
        <div key={label} className={`rounded-2xl border-2 overflow-hidden ${c === "blue" ? "border-blue-300" : "border-red-300"}`} data-testid={`teams-saved-${c === "blue" ? "team1" : "team2"}`}>
          <div className={`px-3 py-2 text-center text-sm font-black text-white ${c === "blue" ? "bg-blue-600" : "bg-red-600"}`}>
            {label} ({(list || []).length})
          </div>
          <ul className="divide-y">
            {(list || []).map((k) => (
              <li key={k.id} className="px-3 py-2 text-sm font-medium text-slate-800 bg-white">{k.name}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

export default function TeamsView({ user, onBack }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekLabel, setWeekLabel] = useState("8/10 – 8/14");
  const [saved, setSaved] = useState(null);
  const [pool, setPool] = useState([]);
  const [team1, setTeam1] = useState([]);
  const [team2, setTeam2] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/teams/pool`);
        setWeekLabel(res.data.week_label || "8/10 – 8/14");
        if (res.data.saved) {
          setSaved(res.data.saved);
        } else {
          setPool(res.data.pool || []);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load campers");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const placeSelected = (team) => {
    if (!selectedId) {
      toast.info("Tap a camper first, then tap a team");
      return;
    }
    const camper = pool.find((c) => c.id === selectedId);
    if (!camper) return;
    setPool(pool.filter((c) => c.id !== selectedId));
    if (team === 1) setTeam1([...team1, camper]);
    else setTeam2([...team2, camper]);
    setSelectedId(null);
  };

  const removeFromTeam = (camper, team) => {
    if (team === 1) setTeam1(team1.filter((c) => c.id !== camper.id));
    else setTeam2(team2.filter((c) => c.id !== camper.id));
    setPool([...pool, camper]);
  };

  const allPlaced = pool.length === 0 && (team1.length > 0 || team2.length > 0);

  const handleSave = async () => {
    if (!allPlaced) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/teams/save`, {
        team1: team1.map((c) => c.id),
        team2: team2.map((c) => c.id),
        group_name: user?.group_name || "",
      });
      setSaved(res.data.saved);
      toast.success("Teams saved!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save teams");
    } finally {
      setSaving(false);
    }
  };

  const TeamColumn = ({ num, list, color }) => (
    <div
      className={`rounded-2xl border-2 overflow-hidden flex flex-col min-h-[180px] ${color === "blue" ? "border-blue-300" : "border-red-300"} ${selectedId ? "ring-2 ring-amber-300 cursor-pointer" : ""}`}
      onClick={() => placeSelected(num)}
      data-testid={`teams-team${num}-column`}
    >
      <div className={`px-3 py-2 text-center text-sm font-black text-white ${color === "blue" ? "bg-blue-600" : "bg-red-600"}`}>
        Team {num} ({list.length})
      </div>
      <div className="flex-1 p-2 space-y-1.5 bg-white">
        {list.length === 0 && (
          <p className="text-xs text-center text-muted-foreground py-4">
            {selectedId ? "Tap here to place" : "Empty"}
          </p>
        )}
        {list.map((c) => (
          <button
            key={c.id}
            onClick={(e) => { e.stopPropagation(); removeFromTeam(c, num); }}
            data-testid={`team${num}-camper-${c.id}`}
            className={`w-full px-2.5 py-2 rounded-lg border text-sm font-semibold text-left transition-all active:scale-95 ${color === "blue" ? "border-blue-200 bg-blue-50 text-blue-900" : "border-red-200 bg-red-50 text-red-900"}`}
          >
            {c.name}
            {c.missing_days && c.missing_days.length > 0 && (
              <span className="block text-[10px] font-normal text-orange-600">not here {c.missing_days.join(", ")}</span>
            )}
          </button>
        ))}
        {list.length > 0 && selectedId && (
          <p className="text-[10px] text-center text-amber-600 font-semibold pt-1">Tap here to place</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="px-2" data-testid="teams-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-indigo-700" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-primary-700">Teams — {user?.group_name || user?.group_code}</h1>
              <p className="text-[10px] text-muted-foreground">Week of {weekLabel}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : saved ? (
          <SavedTeams saved={saved} />
        ) : (
          <>
            <div className="mb-4 p-3 rounded-xl bg-indigo-50 border border-indigo-200">
              <p className="text-xs text-indigo-800 font-semibold">
                Tap a camper below, then tap Team 1 or Team 2 to place them. Tap a placed camper to send them back.
                Every camper must be placed before you can save.
              </p>
            </div>

            <div className="mb-4" data-testid="teams-pool">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-black text-slate-700 uppercase tracking-wide">To Place ({pool.length})</h2>
                {pool.length === 0 && <span className="text-xs font-bold text-emerald-600">All placed ✓</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {pool.map((c) => (
                  <CamperChip
                    key={c.id}
                    camper={c}
                    selected={selectedId === c.id}
                    onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
                    testId={`pool-camper-${c.id}`}
                  />
                ))}
                {pool.length === 0 && team1.length === 0 && team2.length === 0 && (
                  <p className="text-sm text-muted-foreground py-4">No campers scheduled for that week.</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <TeamColumn num={1} list={team1} color="blue" />
              <TeamColumn num={2} list={team2} color="red" />
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 z-40">
              <div className="max-w-3xl mx-auto">
                <Button
                  onClick={handleSave}
                  disabled={!allPlaced || saving}
                  className="w-full h-12 text-base font-bold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  data-testid="teams-save-btn"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  {allPlaced ? "Save Teams" : `Place ${pool.length} more camper${pool.length === 1 ? "" : "s"} to save`}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
